import { useLanguage } from '../../contexts/LanguageContext';
import { useState } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { TabsContent } from '../ui/tabs';
import { FileText, Trash2, Check, Receipt, FileCheck, Download, Pencil, Plus, Info, Mail, Copy, CreditCard, MessageCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { ListPager, usePagedList } from './ListPager';
import { ListToolbar, useFilteredList } from './ListToolbar';
import { PaidInvoicesExport } from './PaidInvoicesExport';
import { PaymentConfirmFields } from './PaymentConfirmFields';


const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InvoicesTab = ({
  invoices, trainingGuides = [], formatDateShort, getAuthHeaders, fetchData,
  setSelectedInvoice, setInvoiceDetailOpen,
  setPaymentConfirmData, setPaymentConfirmOpen,
  setEditingInvoice, setInvoiceFormData, setInvoiceDialogOpen,
  guideTemplates = [], schedules = [],
  setGuideCreateForm, setGuideCreateDialogOpen,
  courses = [],
}) => {
  const { t, language } = useLanguage();
  const [statusChangeOpen, setStatusChangeOpen] = useState(false);
  const [statusChangeData, setStatusChangeData] = useState({ invoiceId: '', newStatus: '', confirmedBy: '', paymentNote: '' });
  const guideAppIds = new Set((trainingGuides || []).map(g => g.applicationId).filter(Boolean));
  const sortedInvoices = [...invoices].sort((a, b) => {
    const statusOrder = { unpaid: 0, paid: 1, cancelled: 2 };
    const sa = statusOrder[a.status] ?? 9;
    const sb = statusOrder[b.status] ?? 9;
    if (sa !== sb) return sa - sb;
    return new Date(b.createdAt) - new Date(a.createdAt);
  });
  const filter = useFilteredList(
    sortedInvoices,
    ['invoiceNumber', 'userName', 'userEmail', 'companyName', 'courseName'],
    [{ key: 'status', label: t('admin.common.status'), options: [
      { value: 'unpaid', label: t('admin.invoices.unpaid') },
      { value: 'paid', label: t('admin.invoices.paid') },
      { value: 'cancelled', label: 'キャンセル' },
    ] }]
  );
  const pager = usePagedList(filter.result);

  const copyPayLink = async (inv) => {
    let link = `${BACKEND_URL}/api/public/invoices/${inv.id}/pay`;
    try {
      const res = await axios.get(`${API}/admin/invoices/${inv.id}/pay-token`, { headers: getAuthHeaders() });
      if (res.data?.token) link = `${link}?token=${res.data.token}`;
    } catch {
      toast.error(t('admin.common.saveFailed'));
      return;
    }
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(link);
      } else {
        const ta = document.createElement('textarea');
        ta.value = link;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      toast.success(t('admin.invoices.payLinkCopied'));
    } catch {
      window.prompt(t('admin.invoices.payLinkCopyManual'), link);
    }
  };

  return (
    <>
    <TabsContent value="invoices" className="p-4">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-gray-800">{t('admin.invoices.title')}</h3>
          <div className="flex gap-2">
            <Button size="sm" className="bg-gray-300 text-gray-500 cursor-not-allowed" data-testid="add-invoice-btn"
              disabled
              title="請求書は申込を「確定」にすると自動で作成されます"
              onClick={() => {}}>
              <Plus className="h-4 w-4 mr-1" /> {t('admin.invoices.create')}
            </Button>
            <Badge variant="outline" className="text-orange-600 border-orange-300">
              {t('admin.invoices.unpaid')}: {invoices.filter(i => i.status === 'unpaid').length}
            </Badge>
            <Badge variant="outline" className="text-green-600 border-green-300">
              {t('admin.invoices.paid')}: {invoices.filter(i => i.status === 'paid').length}
            </Badge>
          </div>
        </div>
        <PaidInvoicesExport getAuthHeaders={getAuthHeaders} />
        <ListToolbar filter={filter} testId="invoices" placeholder="請求書番号・氏名・会社名・講習で検索" />
        {pager.total === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <Receipt className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>{t('admin.invoices.title')}</p>
            <p className="text-sm mt-1"></p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[130px]">{t('admin.invoices.title')}</TableHead>
                  <TableHead>{t('admin.common.name')}</TableHead>
                  <TableHead>{t('admin.certificates.courseName')}</TableHead>
                  <TableHead className="text-right">{t('admin.invoices.amount')}</TableHead>
                  <TableHead className="w-[100px]">{t('admin.common.status')}</TableHead>
                  <TableHead className="w-[100px]">{t('admin.invoices.issueDate')}</TableHead>
                  <TableHead className="w-[140px]">{t('admin.common.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pager.paged.map(inv => {
                  const hasGuide = guideAppIds.has(inv.applicationId);
                  const showGuideBtn = inv.status === 'paid' && !hasGuide;
                  const matchedGuide = (trainingGuides || []).find(g =>
                    (inv.applicationId && g.applicationId === inv.applicationId) ||
                    (g.userEmail && g.userEmail === inv.userEmail && g.courseName === inv.courseName)
                  );
                  return (
                    <TableRow key={inv.id} data-testid={`invoice-row-${inv.id}`}>
                      <TableCell className="font-mono text-xs">{inv.invoiceNumber}</TableCell>
                      <TableCell>
                        <div>{inv.userName}</div>
                        {inv.companyName && <div className="text-xs text-gray-400">{inv.companyName}</div>}
                      </TableCell>
                      <TableCell className="text-sm">{(() => { const cr = courses.find(c => c.name?.ja === inv.courseName) || courses.find(c => c.id === inv.courseId); return cr?.name?.[language] || cr?.name?.ja || inv.courseName || '-'; })()}</TableCell>
                      <TableCell className="text-right font-bold">¥{(inv.totalAmount || 0).toLocaleString()}</TableCell>
                      <TableCell>
                        <select
                          value={inv.status || 'unpaid'}
                          className={`text-xs font-semibold rounded px-2 py-1 border-0 cursor-pointer ${inv.status === 'paid' ? 'bg-green-100 text-green-700' : inv.status === 'cancelled' ? 'bg-gray-100 text-gray-500' : 'bg-orange-100 text-orange-700'}`}
                          data-testid={`invoice-status-${inv.id}`}
                          onChange={(e) => {
                            const newStatus = e.target.value;
                            setStatusChangeData({ invoiceId: inv.id, newStatus, confirmedBy: '', paymentNote: '', paymentMethod: inv.paymentMethod || '' });
                            setStatusChangeOpen(true);
                            e.target.value = inv.status || 'unpaid';
                          }}
                        >
                          <option value="unpaid">{t('admin.invoices.unpaid')}</option>
                          <option value="paid">{t('admin.invoices.paid')}</option>
                          <option value="cancelled">キャンセル</option>
                        </select>
                      </TableCell>
                      <TableCell className="text-xs text-gray-500">{formatDateShort(inv.createdAt)}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost"
                            data-testid={`invoice-pdf-${inv.id}`}
                            title="PDF表示"
                            onClick={async () => {
                              try {
                                const res = await axios.get(`${API}/invoices/${inv.id}/pdf`, {
                                  headers: getAuthHeaders(),
                                  responseType: 'blob'
                                });
                                const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                                window.open(url, '_blank');
                              } catch(e) { toast.error('PDFの表示に失敗しました'); }
                            }}>
                            <FileText className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="ghost"
                            data-testid={`invoice-edit-${inv.id}`}
                            title="編集"
                            onClick={() => {
                              setEditingInvoice(inv);
                              setInvoiceFormData({
                                applicationId: inv.applicationId || '',
                                userId: inv.userId || '',
                                userName: inv.userName || '',
                                userEmail: inv.userEmail || '',
                                userPhone: inv.userPhone || '',
                                userAddress: inv.userAddress || '',
                                companyName: inv.companyName || '',
                                companyAddress: inv.companyAddress || '',
                                companyPhone: inv.companyPhone || '',
                                courseName: inv.courseName || '',
                                items: inv.items || [{ name: '', amount: 0 }],
                                totalAmount: inv.totalAmount || 0,
                                note: inv.note || '',
                                sendEmail: false,
                                dueDate: inv.dueDate || '',
                                hideParticipants: !!inv.hideParticipants,
                                recipientDisplay: inv.recipientDisplay || 'both'
                              });
                              setInvoiceDialogOpen(true);
                            }}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="outline" className="text-blue-600 border-blue-300 hover:bg-blue-50 hover:text-blue-800"
                            data-testid={`invoice-detail-${inv.id}`}
                            onClick={() => { setSelectedInvoice(inv); setInvoiceDetailOpen(true); }}>
                            <Info className="h-3.5 w-3.5 mr-1" />
                            詳細
                          </Button>
                          {matchedGuide && (
                            <Button size="sm" variant="ghost" className="text-blue-600 hover:text-blue-800"
                              data-testid={`invoice-guide-pdf-${inv.id}`}
                              title="時間割（講習案内）を表示"
                              onClick={async () => {
                                try {
                                  const res = await axios.get(`${API}/admin/training-guides/${matchedGuide.id}/pdf`, {
                                    headers: getAuthHeaders(), responseType: 'blob'
                                  });
                                  const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                                  window.open(url, '_blank');
                                } catch(e) { toast.error('時間割の表示に失敗しました'); }
                              }}>
                              <FileCheck className="h-4 w-4" />
                            </Button>
                          )}
                          <Button size="sm" variant="ghost" className="text-green-600 hover:text-green-800"
                            data-testid={`invoice-mail-${inv.id}`}
                            title={t("admin.common.email")}                            onClick={async () => {
                              if (!inv.userEmail) { toast.error(t('admin.common.updateFailed')); return; }
                              if (!window.confirm(`${inv.userEmail} ?`)) return;
                              try {
                                await axios.post(`${API}/admin/invoices/${inv.id}/send-email`, {}, { headers: getAuthHeaders() });
                                toast.success(t('admin.common.updated'));
                              } catch(e) { toast.error(e.response?.data?.detail || t('admin.common.saveFailed')); }
                            }}>
                            <Mail className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="ghost" className="text-emerald-600 hover:text-emerald-800"
                            data-testid={`invoice-whatsapp-${inv.id}`}
                            title="WhatsAppで請求案内を送信"
                            onClick={async () => {
                              if (!inv.userPhone) { toast.error('電話番号が設定されていません'); return; }
                              if (!window.confirm(`${inv.userPhone} 宛てにWhatsAppで請求案内を送信しますか？`)) return;
                              try {
                                await axios.post(`${API}/admin/invoices/${inv.id}/send-whatsapp`, {}, { headers: getAuthHeaders() });
                                toast.success('WhatsAppで送信しました');
                              } catch(e) { toast.error(e.response?.data?.detail || t('admin.common.saveFailed')); }
                            }}>
                            <MessageCircle className="h-4 w-4" />
                          </Button>
                          {inv.status === 'unpaid' && (
                            <Button size="sm" variant="ghost" className="text-[#635bff] hover:text-[#4b45c6]"
                              data-testid={`invoice-copy-paylink-${inv.id}`}
                              title={t('admin.invoices.copyPayLink')}
                              onClick={() => copyPayLink(inv)}>
                              <div className="relative flex items-center">
                                <CreditCard className="h-3.5 w-3.5" />
                                <Copy className="h-3 w-3 ml-0.5" />
                              </div>
                            </Button>
                          )}
                          {inv.status === 'paid' && (
                            <>
                              <Button size="sm" variant="ghost" className="text-[#635bff] hover:text-[#4b45c6]"
                                data-testid={`invoice-receipt-pdf-${inv.id}`}
                                title="領収書PDF"
                                onClick={async () => {
                                  try {
                                    const res = await axios.get(`${API}/invoices/${inv.id}/receipt-pdf`, {
                                      headers: getAuthHeaders(), responseType: 'blob'
                                    });
                                    const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                                    window.open(url, '_blank');
                                  } catch(e) { toast.error(e.response?.data?.detail || '領収書の表示に失敗しました'); }
                                }}>
                                <Receipt className="h-4 w-4" />
                              </Button>
                              <Button size="sm" variant="ghost" className="text-[#635bff] hover:text-[#4b45c6]"
                                data-testid={`invoice-receipt-mail-${inv.id}`}
                                title="領収書をメール送信"
                                onClick={async () => {
                                  if (!inv.userEmail) { toast.error(t('admin.common.updateFailed')); return; }
                                  if (!window.confirm(`${inv.userEmail} に領収書を送信しますか？`)) return;
                                  try {
                                    await axios.post(`${API}/admin/invoices/${inv.id}/send-receipt-email`, {}, { headers: getAuthHeaders() });
                                    toast.success('領収書を送信しました');
                                  } catch(e) { toast.error(e.response?.data?.detail || t('admin.common.saveFailed')); }
                                }}>
                                <div className="relative flex items-center">
                                  <Receipt className="h-4 w-4" />
                                  <Mail className="h-2.5 w-2.5 -ml-1 -mb-2" />
                                </div>
                              </Button>
                            </>
                          )}
                          <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700"
                            data-testid={`invoice-delete-${inv.id}`}
                            title="削除"
                            onClick={async () => {
                              if (!window.confirm(t('admin.common.delete') + '?')) return;
                              try {
                                await axios.delete(`${API}/admin/invoices/${inv.id}`, { headers: getAuthHeaders() });
                                toast.success(t('admin.common.deleted'));
                                fetchData();
                              } catch(e) { toast.error('削除に失敗しました'); }
                            }}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <ListPager pager={pager} testId="invoices" />
          </div>
        )}
      </div>
    </TabsContent>
    <Dialog open={statusChangeOpen} onOpenChange={setStatusChangeOpen}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{t('admin.common.status')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-sm font-medium">{t("admin.invoices.changeTo")}</Label>
            <div className="mt-1">
              <Badge className={statusChangeData.newStatus === 'paid' ? 'bg-green-100 text-green-700' : statusChangeData.newStatus === 'cancelled' ? 'bg-gray-100 text-gray-500' : 'bg-orange-100 text-orange-700'}>
                {statusChangeData.newStatus === 'paid' ? t('admin.invoices.paid') : statusChangeData.newStatus === 'cancelled' ? t('admin.common.cancelled') : t('admin.invoices.unpaid')}
              </Badge>
            </div>
          </div>
          {statusChangeData.newStatus === 'paid' ? (
            <PaymentConfirmFields data={statusChangeData} setData={setStatusChangeData} testPrefix="status-change" />
          ) : (<>
          <div>
            <Label className="text-sm font-medium">{t("admin.invoices.confirmedByLabel")} <span className="text-red-500">*</span></Label>
            <Input value={statusChangeData.confirmedBy} onChange={e => setStatusChangeData(d => ({ ...d, confirmedBy: e.target.value }))} className="mt-1" placeholder={t("admin.invoices.enterConfirmedBy")} />
          </div>
          <div>
            <Label className="text-sm font-medium">{t("admin.form.notes")}</Label>
            <Textarea value={statusChangeData.paymentNote} onChange={e => setStatusChangeData(d => ({ ...d, paymentNote: e.target.value }))} className="mt-1" rows={3} placeholder={t("admin.form.notes")} />
          </div>
          </>)}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setStatusChangeOpen(false)}>キャンセル</Button>
          <Button className="bg-[#00A651] hover:bg-[#008a43]" data-testid="status-change-submit-btn" onClick={async () => {
            if (statusChangeData.newStatus === 'paid' && !statusChangeData.paymentMethod) { toast.error('入金方法を選択してください'); return; }
            if (!statusChangeData.confirmedBy.trim()) { toast.error(t('admin.invoices.enterConfirmedBy')); return; }
            try {
              await axios.put(`${API}/admin/invoices/${statusChangeData.invoiceId}`, {
                status: statusChangeData.newStatus,
                ...(statusChangeData.newStatus === 'paid' ? { paymentMethod: statusChangeData.paymentMethod } : {}),
                confirmedBy: statusChangeData.confirmedBy,
                paymentNote: statusChangeData.paymentNote
              }, { headers: getAuthHeaders() });
              toast.success(t('admin.common.updated'));
              setStatusChangeOpen(false);
              fetchData();

              // 入金済みに変更された場合、講習案内作成画面を開く
              if (statusChangeData.newStatus === 'paid' && setGuideCreateForm && setGuideCreateDialogOpen) {
                const inv = invoices.find(i => i.id === statusChangeData.invoiceId);
                if (inv) {
                  const courseName = inv.courseName || '';
                  const courseNameOnly = courseName.replace(/【[^】]*】/, '').replace(/\s*\(.*\)$/, '').trim();
                  const matchTemplate = (guideTemplates || []).find(t => t.title === courseNameOnly) || (guideTemplates || [])[0];
                  const dateMatch = courseName.match(/\((\d{4}-\d{2}-\d{2})/);
                  const startDate = dateMatch?.[1] || '';
                  const templateTime = (matchTemplate?.meetingTime || '').replace(/^\d{4}-\d{2}-\d{2}\s*/, '').trim() || '09:00';
                  const meetingTime = startDate ? `${startDate} ${templateTime}` : matchTemplate?.meetingTime || '';

                  setGuideCreateForm({
                    applicationId: inv.applicationId || '',
                    templateId: matchTemplate?.id || '',
                    courseName: courseName,
                    schedule: courseName.match(/\(([^)]+)\)/)?.[1] || '',
                    userName: inv.userName || '',
                    userEmail: inv.userEmail || '',
                    venue: matchTemplate?.venue || '',
                    venueAddress: matchTemplate?.venueAddress || '',
                    meetingTime: meetingTime,
                    itemsToBring: matchTemplate?.itemsToBring || '',
                    notes: matchTemplate?.notes || '',
                    mapUrl: matchTemplate?.mapUrl || '',
                    additionalInfo: matchTemplate?.additionalInfo || '',
                    sendEmail: true
                  });
                  setTimeout(() => setGuideCreateDialogOpen(true), 300);
                }
              }
            } catch { toast.error(t('admin.common.updateFailed')); }
          }}>{t('admin.common.update')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
};

export default InvoicesTab;
